"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import ServiceIcon from '@/components/ui/ServiceIcon';

export interface ServiceData {
  id: string;
  slug: string;
  nom: string;
  descriptionCourte: string;
  icone?: string;
}

interface Props {
  services: ServiceData[];
}

export default function ServicesSection({ services }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.service-card-anim');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('reveal-visible');
                card.classList.remove('reveal-hidden');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-20 md:py-28 bg-white" aria-labelledby="services-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Spécialités (para-)médicales
            </p>
            <h2
              id="services-title"
              className="text-3xl md:text-4xl font-semibold text-text-main mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.01em' }}
            >
              Nos spécialités (para-)médicales
            </h2>
            <span className="section-decorator section-decorator-left" aria-hidden="true" />
          </div>
          <Link
            href="/services"
            className="btn-secondary text-sm flex-shrink-0 self-start md:self-end"
            aria-label="Voir toutes nos spécialités médicales"
          >
            Voir toutes les spécialités
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        {/* Services bento grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          ref={sectionRef}
        >
          {services.map((service, index) => {
            const featured = index === 0 || index === 3;
            return (
              <div
                key={service.id}
                className={`service-card-anim reveal-hidden service-card bg-white border border-border rounded-card p-7 shadow-card card-hover group cursor-pointer delay-${(index % 5 + 1) * 100} ${featured ? 'border-l-4 border-l-primary' : ''}`}
                role="article"
                aria-label={`Service : ${service.nom}`}
              >
                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl bg-primary-soft flex items-center justify-center text-primary mb-5 group-hover:bg-primary group-hover:text-white transition-all duration-300"
                  aria-hidden="true"
                >
                  <ServiceIcon slug={service.slug} icone={service.icone} className="w-8 h-8" />
                </div>

                {/* Title */}
                <h3
                  className="text-lg font-semibold text-text-main mb-3 group-hover:text-primary transition-colors"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {service.nom}
                </h3>

                {/* Description */}
                {service.descriptionCourte && (
                  <p
                    className="text-text-muted text-sm leading-relaxed mb-5"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {service.descriptionCourte}
                  </p>
                )}

                {/* Link */}
                <Link
                  href={`/services/${service.slug}`}
                  className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:gap-3 transition-all duration-200"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                  aria-label={`En savoir plus sur ${service.nom}`}
                >
                  En savoir plus
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
